import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { User } from '@comic/shared'
import { authApi } from '../api/auth'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(null)
  const user = ref<User | null>(null)

  const isLoggedIn = computed(() => token.value != null && user.value != null)
  const isSuperuser = computed(() => user.value?.is_superuser === 1)

  async function login(email: string, password: string) {
    const res = await authApi.login({ email, password })
    token.value = res.token
    user.value = res.user
    return res.user
  }

  async function logout() {
    try {
      // Best-effort: the server will accept any token shape here. If we're
      // offline or the token's already gone, that's fine — we still clear.
      if (token.value) await authApi.logout()
    } catch { /* swallow */ }
    clear()
  }

  // Local clear without server call — used by the 401 interceptor.
  function clear() {
    token.value = null
    user.value = null
  }

  async function refreshMe() {
    if (!token.value) return null
    try {
      user.value = await authApi.me()
      return user.value
    } catch {
      clear()
      return null
    }
  }

  return { token, user, isLoggedIn, isSuperuser, login, logout, clear, refreshMe }
}, {
  persist: true,
})
